import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'

import styled from 'styled-components'

import { LoadingIcon } from 'src/components/atoms'
import { ProjectListZero, TableSortType } from 'src/components/molecules'
import { BulkActionButton } from 'src/components/molecules/inputs/BulkActionButton'
import {
  ProjectListBody,
  ProjectListHead,
  ProjectSortItemType,
  ProjectsBulkAssignModal,
} from 'src/components/organisms'

import { useAuthority } from 'src/hooks'
import { comparePrefectures } from 'src/libs/helper'
import { ProfileEntity, ProjectEntity, SelectableProjectEntity } from 'src/types'

const HeadContainer = styled.div`
  border-radius: 8px 8px 0 0;

  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  overflow: hidden;
`

const BodyContainer = styled.div`
  border-radius: 0 0 8px 8px;

  border-top: none;
  overflow: hidden;
`

type Props = {
  projects: ProjectEntity[]
  profile: ProfileEntity | undefined
  isLoading: boolean
  className?: string
}

export const ProjectListTable: FC<Props> = memo(({ projects, profile, isLoading, className }) => {
  const [selectableProjects, setSelectableProjects] = useState<SelectableProjectEntity[]>(
    projects.map(project => ({ ...project, isSelected: false }))
  )
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)

  const [sortItem, setSortItem] = useState<ProjectSortItemType>('createdAt')
  const [sortType, setSortType] = useState<TableSortType>('desc')

  const { canAssignProject } = useAuthority()
  const bulkActionOptions = []
  if (canAssignProject) {
    bulkActionOptions.push({
      text: 'ユーザーアサイン',
      iconPath: 'plus/soft/line',
      action: () => setShowBulkAssignModal(true),
    })
  }

  const sortedProjects = useMemo(() => {
    const order = sortType === 'desc' ? 1 : -1

    if (sortItem === 'status') {
      return [...selectableProjects].sort((a, b) => {
        // 削除中の場合は必ず最後に持ってくる
        if (a.isDeleting && !b.isDeleting) {
          return 1
        }
        if (!a.isDeleting && b.isDeleting) {
          return -1
        }
        return a.status.priority < b.status.priority ? order : -order
      })
    }
    if (sortItem === 'creator') {
      return [...selectableProjects].sort((a, b) => {
        return (a.creator.name || '') < (b.creator.name || '') ? order : -order
      })
    }

    if (sortItem === 'prefecture') {
      return [...selectableProjects].sort((a, b) => {
        return comparePrefectures(a.prefecture, b.prefecture) * order
      })
    }

    // 数値の場合は undefined を 0 として並び替えを行う（0としないと意図しない動きになってしまう）
    const numberKeys = [
      'structureCount',
      'roomCount',
      'planCount',
      'averagePriceRange',
      'purchaserCount',
      'applicationCount',
    ]
    if (numberKeys.includes(sortItem)) {
      return [...selectableProjects].sort((a, b) => {
        return (a[sortItem]! ?? 0) < (b[sortItem]! ?? 0) ? order : -order
      })
    }

    // 選択されている項目に応じてソートを行う
    return [...selectableProjects].sort((a, b) => {
      return a[sortItem]! < b[sortItem]! ? order : -order
    })
  }, [selectableProjects, sortItem, sortType])

  const handleSelectSortItem = useCallback(
    (newItem: ProjectSortItemType) => {
      if (sortItem === newItem) {
        const newSortType = sortType === 'desc' ? 'asc' : 'desc'
        setSortType(newSortType)
        return
      }

      setSortType('desc')
      setSortItem(newItem)
    },
    [sortItem, sortType]
  )

  const handleOnSelectProject = useCallback(
    (project: SelectableProjectEntity) => {
      const updatedProjects = selectableProjects.map(p => {
        if (p.id === project.id) {
          return { ...p, isSelected: !p.isSelected }
        }
        return p
      })
      setSelectableProjects(updatedProjects)
    },
    [selectableProjects]
  )

  const handleAfterAssigned = () => {
    setShowBulkAssignModal(false)
    const updatedProjects = projects.map(project => ({ ...project, isSelected: false }))
    setSelectableProjects(updatedProjects)
  }

  const handleOnClickAllSelect = () => {
    const isAllSelected = selectableProjects
      .filter(p => !p.isDeleting)
      .every(project => project.isSelected)

    const updatedProjects = projects.map(project => {
      if (project.isDeleting) {
        return { ...project, isSelected: false }
      }
      return { ...project, isSelected: !isAllSelected }
    })
    setSelectableProjects(updatedProjects)
  }

  useEffect(() => {
    const updatedProjects = projects.map(project => ({ ...project, isSelected: false }))
    setSelectableProjects(updatedProjects)
  }, [projects])

  return (
    <div className={className}>
      <div>
        <HeadContainer className="tw-border tw-border-border-secondary">
          <ProjectListHead
            selectableProjects={selectableProjects}
            sortItem={sortItem}
            sortType={sortType}
            onSelectItem={handleSelectSortItem}
            onClickAllSelect={handleOnClickAllSelect}
          />
        </HeadContainer>
        {isLoading || !profile ? (
          <div className="tw-centering">
            <LoadingIcon className="tw-text-2xl tw-mt-s2" />
          </div>
        ) : selectableProjects.length > 0 ? (
          <BodyContainer className="tw-border tw-border-border-secondary">
            <ProjectListBody
              projects={sortedProjects}
              profile={profile}
              onSelectProject={handleOnSelectProject}
            />
          </BodyContainer>
        ) : (
          <div className="tw-w-full tw-centering tw-mt-s3">
            <ProjectListZero />
          </div>
        )}
      </div>

      {showBulkAssignModal && profile && (
        <ProjectsBulkAssignModal
          targetProjectIds={selectableProjects
            .filter(project => project.isSelected)
            .map(project => project.id)}
          profile={profile}
          isOpen={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          afterAssigned={handleAfterAssigned}
        />
      )}

      <BulkActionButton
        isActive={selectableProjects.some(project => project.isSelected)}
        actions={bulkActionOptions}
      />
    </div>
  )
})
